<script>
import { mapGetters } from 'vuex'
import { bufferRing } from '@/services/geo/polygonBuffer'
import { modeForRadius } from '@/services/geo/searchAreaMode'

/**
 * Renderless map overlay component.
 *
 * Reads the server-resolved search area from Vuex (populated by garden/
 * filterGardens from the gardens response's `search_area` block) and draws:
 *
 *   - basePolygon: the county outline (blue), shown whenever the API
 *     returned a search_area.
 *   - expandedPolygon: a turf-buffered ring (red), shown when the user
 *     picked radius 1mi or 5mi. The buffer is computed LOCALLY here via
 *     @turf/buffer so the visual matches a true geometric buffer; the
 *     same ring is sent back to the API as `filter[expanded_polygon]`
 *     by the gardens fetch so the ES filter agrees with the overlay.
 *   - circle: a google.maps.Circle (red, circle-mode only - when the user
 *     picked radius 10mi or more). Centred on the polygon centroid.
 *
 * The expanded polygon and the circle are mutually exclusive; whichever
 * one matches the current radius is shown alongside the base polygon.
 *
 * The component renders nothing until base paths are present (no
 * "incorrect content first" flash; boundary and pins land together).
 */
export default {
  name: 'SearchAreaPolygon',

  props: {
    google: {
      type: Object,
      required: true,
    },
    map: {
      type: Object,
      required: true,
    },
  },

  render() {},

  computed: {
    ...mapGetters('map', [
      'searchAreaBasePaths',
      'searchAreaCircle',
    ]),
    ...mapGetters('location', ['selectedRadius']),

    /**
     * The buffered ring used for the red expansion overlay. Computed
     * locally via turf so it's a true geometric buffer (vs the radial
     * approximation we used to ship from the API).
     *
     * Returns null when:
     *   - no base polygon is available yet
     *   - the radius is 0 (no expansion)
     *   - the radius is > 5mi (we're in circle mode, not polygon mode)
     */
    expandedPaths() {
      if (!this.searchAreaBasePaths) return null
      const miles = Number(this.selectedRadius?.value)
      if (modeForRadius(miles) !== 'polygon') return null
      return bufferRing(this.searchAreaBasePaths, miles)
    },
  },

  watch: {
    searchAreaBasePaths: {
      immediate: true,
      handler(paths) {
        this.renderBasePolygon(paths)
      },
    },

    expandedPaths: {
      immediate: true,
      handler(paths) {
        this.renderExpandedPolygon(paths)
      },
    },

    searchAreaCircle: {
      immediate: true,
      handler(circle) {
        this.renderCircle(circle)
      },
    },
  },

  beforeDestroy() {
    this.removeBasePolygon()
    this.removeExpandedPolygon()
    this.removeCircle()
  },

  methods: {
    renderBasePolygon(paths) {
      if (!paths || paths.length < 3) {
        this.removeBasePolygon()
        return
      }
      if (this.basePolygon) {
        this.basePolygon.setPaths(paths)
      } else {
        this.basePolygon = new this.google.maps.Polygon({
          paths,
          strokeColor: '#1E88E5',
          strokeOpacity: 1,
          strokeWeight: 2,
          fillOpacity: 0.1,
          fillColor: '#1E88E5',
          clickable: false,
          map: this.map,
        })
      }
    },

    removeBasePolygon() {
      if (this.basePolygon) {
        this.basePolygon.setMap(null)
        this.basePolygon = null
      }
    },

    renderExpandedPolygon(paths) {
      if (!paths || paths.length < 3) {
        this.removeExpandedPolygon()
        return
      }
      if (this.expandedPolygon) {
        this.expandedPolygon.setPaths(paths)
      } else {
        this.expandedPolygon = new this.google.maps.Polygon({
          paths,
          strokeColor: '#E53935',
          strokeOpacity: 0.8,
          strokeWeight: 1,
          fillOpacity: 0,
          clickable: false,
          map: this.map,
        })
      }
    },

    removeExpandedPolygon() {
      if (this.expandedPolygon) {
        this.expandedPolygon.setMap(null)
        this.expandedPolygon = null
      }
    },

    renderCircle(circle) {
      if (!circle?.center || !Number.isFinite(circle.radiusMeters) || circle.radiusMeters <= 0) {
        this.removeCircle()
        return
      }
      const options = {
        center: circle.center,
        radius: circle.radiusMeters,
        strokeColor: '#E53935',
        strokeOpacity: 0.8,
        strokeWeight: 1,
        fillOpacity: 0,
        clickable: false,
        map: this.map,
      }
      if (this.circle) {
        this.circle.setOptions(options)
      } else {
        this.circle = new this.google.maps.Circle(options)
      }
    },

    removeCircle() {
      if (this.circle) {
        this.circle.setMap(null)
        this.circle = null
      }
    },
  },
}
</script>
